// ═══════════════════════════════════════════════════════════════
// OcRetrospectiva.jsx — aba "Retrospectiva"
//
// O QUE ESTA TELA RESPONDE
//
// As outras abas respondem "quem esta ruim agora". Esta responde a pergunta que
// decide se as outras valem alguma coisa: **o que a PD dizia ANTES de quem
// quebrou**. E a alegacao que um comite de credito testa, e a unica que
// transforma um painel em argumento.
//
// O backend (`services/oc/oc_deterioracao.js`, `retrospectiva`) ja estava
// pronto, testado e em producao desde a v3.45.1 — e sem nenhuma tela que o
// chamasse. Este arquivo existe para isso.
//
// ──────────────────────────────────────────────────────────────
// CINCO RECUSAS. As duas primeiras sao a razao de ser do arquivo.
//
// 1. NUNCA mostrar a sensibilidade sozinha.
//
//    "Antecipamos 8 de 11" e um numero de vendedor. Sem a taxa de falso
//    positivo DO MESMO CORTE ele nao significa nada: um corte de PD >= 0,1%
//    antecipa 100% dos eventos e marca o universo inteiro. O proprio backend
//    escreve isso no campo `aviso` da resposta.
//
//    Entao esta tela faz DUAS consultas e as mostra lado a lado, sempre:
//    a retrospectiva no corte X, e quantas companhias SEM ato publico estao
//    acima do mesmo corte X hoje. Mexer no corte move os dois numeros juntos,
//    na frente do cliente. E o unico jeito honesto de mostrar isto.
//
// 2. NUNCA esconder quem passou batido.
//
//    As companhias que quebraram SEM nunca cruzar o corte sao o que o comite
//    vai perguntar. Elas ganham secao propria, com nome, e nao ficam diluidas
//    no fim de uma tabela. Material de venda que esconde o erro nao sobrevive
//    a primeira reuniao tecnica — e sobreviver a ela e o objetivo.
//
// 3. NUNCA usar a data-base como "a data em que sabiamos".
//
//    O balanco de 31/03 nao esta publicado em 31/03. O RiskMind so podia ter
//    calculado depois do prazo do ITR. O backend usa `observacao = data-base +
//    dias_ate_observacao` e a tela IMPRIME essa regra, porque ela e a diferenca
//    entre um lead real e um lead que a gente se atribuiu.
//
// 4. NUNCA dizer "antecipou" sem dizer de quantas. Denominador junto do
//    numerador, como em todas as outras abas desta base.
//
// 5. NUNCA prometer que o retrospectivo e o desempenho futuro. Ele e sobre
//    quem JA quebrou, num acervo de companhias abertas, com eventos que a CVM
//    registrou. A tela diz o que a amostra e.
// ═══════════════════════════════════════════════════════════════

import React, { useCallback, useEffect, useState } from 'react';

const C = {
  navy: '#0A1628', blue: '#1A3F6F', amber: '#C87B2A', gold: '#E8C56D',
  red: '#DC2626', green: '#15803D', amber2: '#D97706', roxo: '#8067B7',
  muted: '#6b7280', line: '#e5e7eb', ink: '#0f172a', paper: '#ffffff',
};

const TIPO_EVENTO = {
  rj_re: 'recuperação judicial ou extrajudicial',
  falencia: 'falência',
  liquidacao: 'liquidação',
  chapter_11: 'Chapter 11',
};
const TIPO_CURTO = { rj_re: 'recuperação', falencia: 'falência', liquidacao: 'liquidação', chapter_11: 'Chapter 11' };

const fmtPct = (v) => (v == null ? '—' : (+v).toFixed(2) + '%');
const fmtData = (d) => {
  if (!d) return '—';
  const s = String(d).slice(0, 10).split('-');
  return s.length === 3 ? `${s[2]}/${s[1]}/${s[0]}` : String(d);
};
/* Dias viram meses quando passam de um trimestre: "412 dias antes" obriga o
   leitor a dividir por 30 de cabeca no meio de uma reuniao. */
const fmtAntecedencia = (dias) => {
  if (dias == null) return '—';
  if (dias < 45) return `${dias} dias antes`;
  const m = Math.round(dias / 30.44);
  return m < 24 ? `${m} meses antes` : `${(dias / 365.25).toFixed(1)} anos antes`;
};

const caixa = {
  background: C.paper, border: `1px solid ${C.line}`, borderLeft: `3px solid ${C.amber}`,
  borderRadius: 10, padding: '10px 14px', fontSize: 12.5, color: C.ink,
  lineHeight: 1.55, marginBottom: 12,
};

const errBox = {
  background: '#fef2f2', border: '1px solid #fecaca', color: '#991b1b',
  borderRadius: 10, padding: '12px 14px', fontSize: 13, marginBottom: 12,
};

const inp = {
  padding: '6px 9px', border: `1px solid ${C.line}`, borderRadius: 8,
  fontSize: 13, color: C.ink, background: C.paper, outline: 'none', width: 74,
};

/* ─────────────────────────────────────── o par que nunca anda sozinho ─ */

/**
 * RECUSA 1. Os dois números, do mesmo tamanho, na mesma caixa.
 *
 * A tentação de design aqui é óbvia: a sensibilidade em fonte grande e o falso
 * positivo numa nota de rodapé. Seria mais bonito e seria desonesto — o custo
 * do corte é metade da informação, não uma ressalva.
 */
function OParDeNumeros({ r, universo, corte, carregando }) {
  const sens = r.sensibilidade;
  const acima = universo?.n_acima_sem_evento;
  const universoN = universo?.n_universo;

  return (
    <div style={{ ...caixa, borderLeftColor: C.navy, display: 'flex', gap: 28, flexWrap: 'wrap', alignItems: 'flex-start' }}>
      <div style={{ flex: '1 1 260px' }}>
        <div style={{ fontSize: 11.5, textTransform: 'uppercase', letterSpacing: 0.5, color: C.muted, fontWeight: 600 }}>
          o que a PD pegou
        </div>
        <div style={{ fontSize: 26, fontWeight: 800, color: C.navy, lineHeight: 1.25, marginTop: 2 }}>
          {r.marcaram_acima_do_corte} de {r.com_serie_publicada_antes}
        </div>
        <div style={{ color: C.muted }}>
          companhias que pediram {TIPO_CURTO.rj_re}, falência ou liquidação já liam
          PD ≥ {corte}% <b>antes</b> do pedido
          {sens != null && <> — {sens}%</>}.
        </div>
      </div>

      <div style={{ flex: '1 1 260px' }}>
        <div style={{ fontSize: 11.5, textTransform: 'uppercase', letterSpacing: 0.5, color: C.muted, fontWeight: 600 }}>
          o que esse corte custa
        </div>
        <div style={{ fontSize: 26, fontWeight: 800, color: acima == null ? C.muted : C.amber2, lineHeight: 1.25, marginTop: 2 }}>
          {carregando && acima == null ? '…' : acima == null ? '—' : acima}
        </div>
        <div style={{ color: C.muted }}>
          {acima == null
            ? 'não consegui contar quantas companhias sem ato público estão acima deste corte agora.'
            : <>companhias <b>sem</b> ato público na CVM estão acima de {corte}% hoje
              {universoN != null && <>, de {universoN} na data-base corrente</>}. Cada uma é uma
              visita, um telefonema ou um limite revisto.</>}
        </div>
      </div>
    </div>
  );
}

/* ───────────────────────────────────────────── quem passou batido ─ */

/* RECUSA 2. Secao propria, com nome. */
function QuemPassouBatido({ casos, corte }) {
  const perdidos = casos.filter((c) => !c.marcou_acima_do_corte);
  if (!perdidos.length) {
    return (
      <div style={{ ...caixa, borderLeftColor: C.green }}>
        <b>Nenhuma passou batido neste corte.</b> Todas as companhias com série publicada antes do
        evento cruzaram {corte}% em algum trimestre anterior ao pedido.
      </div>
    );
  }
  return (
    <section style={{ marginBottom: 26 }}>
      <h3 style={{ margin: '0 0 8px', fontSize: 15, color: C.navy }}>
        Quem passou batido
        <span style={{ fontSize: 12.5, color: C.muted, fontWeight: 400 }}>
          {' '}— <b>{perdidos.length}</b> de {casos.length} nunca cruzou {corte}% antes do pedido
        </span>
      </h3>
      <div style={{ ...caixa, borderLeftColor: C.red }}>
        Estas são as que o modelo <b>não</b> marcou. Elas aparecem com nome de propósito: um
        retrospectivo que mostra só os acertos não sobrevive à primeira reunião técnica, e é
        exatamente nesta lista que um comitê de crédito vai olhar primeiro.
        <div style={{ color: C.muted, marginTop: 4 }}>
          Uma leitura baixa antes do pedido tem causas diferentes — balanço antigo, setor sem par
          comparável, deterioração rápida demais para o trimestre capturar — e elas levam a
          conversas diferentes. A ficha de cada companhia mostra a série inteira.
        </div>
      </div>
      <Tabela casos={perdidos} corte={corte} realcarUltima />
    </section>
  );
}

/* ─────────────────────────────────────────────────────────── tabela ─ */

function Tabela({ casos, corte, realcarUltima }) {
  if (!casos.length) {
    return <div style={{ color: C.muted, fontSize: 13, padding: 20, textAlign: 'center' }}>nenhum caso.</div>;
  }
  const cols = ['companhia', 'ato público', 'quando', realcarUltima ? 'última leitura antes' : 'primeiro sinal ≥ corte', 'antecedência', 'trimestres publicados antes'];
  return (
    <div style={{ background: C.paper, border: `1px solid ${C.line}`, borderRadius: 12, overflow: 'hidden' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
        <thead>
          <tr style={{ background: '#f8fafc' }}>
            {cols.map((c, i) => (
              <th key={c} style={{ textAlign: i >= 4 ? 'right' : 'left', padding: '9px 12px', color: C.muted, fontWeight: 600, fontSize: 11.5, textTransform: 'uppercase', letterSpacing: 0.4, borderBottom: `1px solid ${C.line}`, whiteSpace: 'nowrap' }}>
                {c}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {casos.map((c) => {
            /* Na secao dos acertos mostra o PRIMEIRO sinal (o lead real).
               Na secao de quem passou batido nao existe primeiro sinal — o que
               importa la e a ULTIMA leitura antes do pedido, que e o numero que
               o modelo tinha na mao e nao usou. */
            const alvo = realcarUltima ? c.ultima_leitura_antes : (c.primeiro_sinal || c.ultima_leitura_antes);
            const marcou = c.marcou_acima_do_corte;
            return (
              <tr key={c.cnpj} style={{ borderBottom: '1px solid #f1f5f9' }}>
                <td style={{ padding: '9px 12px' }}>
                  <span style={{ fontWeight: 600 }}>{c.nome || c.cnpj}</span>
                </td>
                <td style={{ padding: '9px 12px', color: C.red, fontSize: 12 }}>
                  {TIPO_EVENTO[c.evento.tipo] || c.evento.tipo}
                </td>
                <td style={{ padding: '9px 12px', color: C.muted, whiteSpace: 'nowrap' }}>
                  {fmtData(c.evento.data)}
                </td>
                <td style={{ padding: '9px 12px', fontVariantNumeric: 'tabular-nums' }}>
                  <b style={{ color: marcou && !realcarUltima ? C.red : C.ink }}>{fmtPct(alvo?.pd_pct)}</b>
                  {alvo?.rating && <span style={{ color: C.muted, fontSize: 11.5 }}> · {alvo.rating}</span>}
                  {/* RECUSA 3: a data que vale e a da OBSERVACAO, e ela sai escrita. */}
                  <div style={{ fontSize: 10.5, color: C.muted, whiteSpace: 'nowrap' }}
                    title="Data-base do balanço, e a data em que essa leitura passou a existir — depois do prazo de entrega do ITR. É a segunda que conta como antecipação.">
                    balanço de {fmtData(alvo?.data_base)} · publicado {fmtData(alvo?.observacao)}
                  </div>
                </td>
                <td style={{ padding: '9px 12px', textAlign: 'right', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap', fontWeight: 600, color: realcarUltima ? C.muted : C.green }}>
                  {fmtAntecedencia(alvo?.dias_antes_do_evento)}
                </td>
                <td style={{ padding: '9px 12px', textAlign: 'right', color: C.muted, fontVariantNumeric: 'tabular-nums' }}>
                  {c.trimestres_publicados_antes}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════ */

export default function OcRetrospectiva({ api }) {
  const [corte, setCorte] = useState(15);
  const [r, setR] = useState(null);
  const [universo, setUniverso] = useState(null);
  const [err, setErr] = useState(null);
  const [carregando, setCarregando] = useState(false);

  /* AS DUAS CONSULTAS ANDAM JUNTAS, e isso e a RECUSA 1 virada codigo.
     Se fossem dois efeitos separados, um corte novo mostraria a sensibilidade
     nova ao lado do falso positivo VELHO por alguns instantes — e o par que a
     tela promete deixaria de ser um par exatamente no momento em que alguem
     esta mexendo no numero para entender o trade-off.

     O universo vai com `escopo=universo` de proposito: o custo do corte e uma
     propriedade do acervo inteiro, nao da carteira de quem esta olhando. Sao
     contagens, nao PDs — a mascara do servidor continua valendo linha a linha. */
  const buscar = useCallback((c) => {
    setCarregando(true); setErr(null);
    return Promise.all([
      api(`/api/oc/deterioracao/retrospectiva?corte=${c}`),
      api(`/api/oc/deterioracao?cortes=${c}&escopo=universo`).catch(() => null),
    ])
      .then(([retro, det]) => {
        setR(retro);
        const lista = det && det.listas ? det.listas[`pd_acima_${Math.round(c)}`] : null;
        setUniverso(lista
          ? {
            n_acima_sem_evento: Array.isArray(lista.sem_evento_publico) ? lista.sem_evento_publico.length : null,
            n_universo: det.universo ? det.universo.n : null,
          }
          : null);
      })
      .catch((e) => setErr(e))
      .finally(() => setCarregando(false));
  }, [api]);

  useEffect(() => {
    let vivo = true;
    const t = setTimeout(() => { if (vivo) buscar(corte); }, 300);
    return () => { vivo = false; clearTimeout(t); };
  }, [corte, buscar]);

  if (err) {
    const gate = err.status === 401 || err.status === 403;
    return (
      <div style={errBox}>
        {gate
          ? 'Esta tela faz parte do Capital Aberto. Se o seu plano inclui e ela não abriu, fale com o suporte.'
          : `Não consegui carregar a retrospectiva: ${err.message}`}
      </div>
    );
  }
  if (!r) return <div style={{ color: C.muted, fontSize: 13, padding: 20 }}>carregando retrospectiva…</div>;

  const acertos = (r.casos || []).filter((c) => c.marcou_acima_do_corte);

  return (
    <div style={{ opacity: carregando ? 0.75 : 1, transition: 'opacity 120ms' }}>
      <div style={{ ...caixa, borderLeftColor: C.gold }}>
        <div style={{ fontWeight: 700, marginBottom: 6 }}>O que a PD dizia antes de quem quebrou</div>
        <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', alignItems: 'center' }}>
          <label style={{ fontSize: 12.5 }}>
            Corte de PD:{' '}
            <input type="number" min="1" max="99" step="1" value={corte} style={inp}
              onChange={(e) => setCorte(Math.max(1, Math.min(99, Number(e.target.value) || 1)))} />{' %'}
          </label>
          {[10, 15, 20, 30].map((v) => (
            <button key={v} onClick={() => setCorte(v)}
              style={{
                padding: '4px 11px', borderRadius: 999, fontSize: 12, cursor: 'pointer',
                border: `1px solid ${corte === v ? C.navy : C.line}`,
                background: corte === v ? C.navy : C.paper,
                color: corte === v ? '#fff' : C.ink,
              }}>
              {v}%
            </button>
          ))}
          {carregando && <span style={{ fontSize: 12, color: C.muted }}>consultando…</span>}
        </div>
        <div style={{ color: C.muted, marginTop: 7 }}>
          Mexer no corte move <b>os dois</b> números abaixo. Um corte mais baixo pega mais
          companhias que quebraram — e marca mais companhias que não quebraram. É esse o
          trade-off, e ele é da sua mesa, não do modelo.
        </div>
      </div>

      <OParDeNumeros r={r} universo={universo} corte={corte} carregando={carregando} />

      {/* RECUSA 3 e 5: de onde vem a amostra e o que "antes" quer dizer. */}
      <div style={{ ...caixa, borderLeftColor: C.roxo }}>
        <b>A data que conta não é a do balanço.</b> O balanço de 31/03 não existe publicado em
        31/03. O RiskMind só poderia ter calculado a PD depois do prazo de entrega do ITR — então
        a antecedência é medida da <b>data de publicação</b> ({r.dias_ate_observacao} dias após a
        data-base) até o ato na CVM, nunca da data-base. Sem essa disciplina a gente se atribuiria
        meses de lead que não teve.
        <div style={{ color: C.muted, marginTop: 5 }}>
          {/* RECUSA 4: denominador junto. */}
          Amostra: <b>{r.eventos_nao_censurados}</b> companhias com ato público registrado na CVM,
          sem contar as que já estavam nessa situação antes da janela de coleta abrir.
          {r.sem_serie_utilizavel > 0 && (
            <> Destas, <b>{r.sem_serie_utilizavel}</b> não têm leitura publicada antes do evento e
              ficam fora da conta — incluí-las como acerto ou como erro seria inventar.</>
          )}
        </div>
      </div>

      <QuemPassouBatido casos={r.casos || []} corte={corte} />

      <section style={{ marginBottom: 26 }}>
        <h3 style={{ margin: '0 0 8px', fontSize: 15, color: C.navy }}>
          Quem o modelo marcou antes
          <span style={{ fontSize: 12.5, color: C.muted, fontWeight: 400 }}>
            {' '}— <b>{acertos.length}</b> de {r.com_serie_publicada_antes}, com a antecedência de cada uma
          </span>
        </h3>
        <Tabela casos={acertos} corte={corte} />
      </section>

      {r.aviso && (
        <div style={{ ...caixa, borderLeftColor: C.muted }}>{r.aviso}</div>
      )}
    </div>
  );
}
